import { Component, inject, PLATFORM_ID } from '@angular/core';
import { PaginationComponent } from "../../components/pagination/pagination.component";
import { SearchComponent } from "../../shared/search/search.component";
import { ExportButtonComponent } from "../../components/buttons/export-button/export-button.component";
import { ImagesService } from '../../services/general/images.service';
import {
  InstitutionTabelComponent
} from "../../components/card/institution/institution-tabel/institution-tabel.component";
import { AddInstitutionComponent } from "../../components/card/institution/add-institution/add-institution.component";
import { InstitutionFilterComponent } from "../../shared/filter/institution-filter/institution-filter.component";
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { Institution, InstitutionQueryParams } from '../../interfaces/responses/institution.interface';
import { InstitutionService } from '../../services/institution/institution.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ProgressLoaderComponent } from "../../components/loaders/progress-loader/progress-loader.component";
import { ToastService } from '../../services/general/toast.service';
import { AddMembersComponent } from "../../components/overlays/add-members/add-members.component";
import { ExportService } from '../../services/general/export.service';
import { PermissionService } from '../../services/permission/permission.service';

@Component({
  selector: 'app-institutions',
  imports: [
    PaginationComponent,
    ExportButtonComponent,
    InstitutionTabelComponent,
    AddInstitutionComponent,
    InstitutionFilterComponent,
    CommonModule,
    ReactiveFormsModule,
    ProgressLoaderComponent,
    AddMembersComponent
  ],
  templateUrl: './institutions.component.html',
  styleUrl: './institutions.component.scss'
})
export class InstitutionsComponent {
  _images = inject(ImagesService);
  permissionService = inject(PermissionService);
  isAddInstitution: boolean = false;
  viewMode: 'table' | 'grid' = 'table';
  institutionService = inject(InstitutionService);
  allInstitutions: PaginationInterface<Institution[]> = { data: [], meta: { page: 1, perPage: 10, total: 6, totalPages: 1 } };
  platformId = inject(PLATFORM_ID);
  searchQuery!: FormGroup;
  _fb = inject(FormBuilder);
  exportService = inject(ExportService);
  isInstitutionLoading: boolean = false;
  isSearchLoading: boolean = false;
  _toast = inject(ToastService);
  isUpdateInstitution: boolean = false;
  isAddMembers: boolean = false;
  queryParams: InstitutionQueryParams = {
    keyword: '',
    date: '',
    endDate: '',
    isActive: null,
    productId: '',
    paginate: true,
    perPage: 10,
    page: 1,
  }

  itemsPerPage: number = 10;
  currentPage: number = 1;
  institution: Institution | null = null;

  institutions: Institution[] = [];

  ngOnInit(): void {
    this.searchQuery = this._fb.group({
      search: [''],
    });

    this.getPaginatedInstitutions();
    if (isPlatformBrowser(this.platformId)) {
      this.searchQuery.get('search')?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((query: string) => {
          this.queryParams.keyword = query.trim();
          this.getPaginatedInstitutions();
        });
    }
  }

  getPaginatedInstitutions() {
    this.isInstitutionLoading = true;
    this.institutionService.getPaginatedInstitutions(this.queryParams).subscribe({
      next: (response) => {
        this.allInstitutions = response;
        this.institutions = response.data || [];
        this.isInstitutionLoading = false;
      },
      error: (error) => {
        console.error('Error fetching institutions:', error);
        this.isInstitutionLoading = false;
      }
    });
  }

  resetFilters() {
    // Reset query params to initial state
    this.queryParams.date = '';
    this.queryParams.endDate = '';
    this.queryParams.isActive = null;
    this.queryParams.page = 1;
    this.getPaginatedInstitutions();
  }

  filterInstitutionByDate(formData: FormGroup) {
    const startDate = formData.get('startDate')?.value;
    const endDate = formData.get('endDate')?.value;
    const status = formData.get('status')?.value;

    // Store the date range for filtering
    this.queryParams.date = startDate || '';
    this.queryParams.endDate = endDate || '';

    // Handle status filter
    if (status) {
      // Convert "Active" to true, "Inactive" to false
      this.queryParams.isActive = status === 'Active' ? true : status === 'Inactive' ? false : null;
    } else {
      this.queryParams.isActive = null;
    }

    this.getPaginatedInstitutions();
  }

  addMember() {
    this.isAddMembers = true;
  }

  onCloseAddMember() {
    this.isAddMembers = false;
  }

  updateInstitution(institution: Institution) {
    const id = institution?.id;
    if (!id || !institution) {
      console.error('Institution or Institution ID is undefined. Cannot update institution.');

      return;
    }

    const formData = new FormData();
    if (institution.name) formData.append('name', institution.name);
    if (institution.address) formData.append('address', institution.address);
    if (institution.contact) formData.append('contact', institution.contact);
    if (institution.description) formData.append('description', institution.description);
    if (institution.email) formData.append('email', institution.email);
    if (institution.autoRepayment !== undefined) {
      formData.append('autoRepayment', institution.autoRepayment ? 'Yes' : 'No');
    }

    this.institutionService.updateInstitution(id, formData).subscribe({
      next: (response) => {
        this._toast.success('Institution updated successfully');
        this.getPaginatedInstitutions();
      },
      error: (error) => {
        console.error('Error updating institution:', error);
        this._toast.error('Error updating institution');
      }
    });
  }

  onShowOverlay() {
    if (this.institution) {
      this.isUpdateInstitution = true;
    }
  }

  onUpdateIntitution(institution: Institution) {
    this.institution = institution;
  }

  // Close handler specifically for the update overlay
  onCloseUpdateInstitution() {
    this.isUpdateInstitution = false;
    this.institution = null;
  }

  get paginatedItems(): Institution[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.institutions.slice(start, end);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getPaginatedInstitutions();
  }

  onAddInstitution() {
    this.isAddInstitution = true;
  }

  onCloseInstitution() {
    this.institution = null;
    this.isAddInstitution = false;
  }

  /**
   * Handle export based on type
   */
  handleExport(type: 'Excel' | 'PDF' | 'CSV'): void {
    if (this.institutions.length === 0) {
      this._toast.warning('No data available to export');
      return;
    }

    const exportData = this.institutions.map(institution => ({
      'Institution Name': institution.name || '',
      'Code': institution.code || '',
      'Address': institution.address || '',
      'Contact': institution.contact || '',
      'Email': institution.email || '',
      'Description': institution.description || '',
      'Assigned To': institution.assignedTo ? `${institution.assignedTo.firstName} ${institution.assignedTo.lastName}` : '',
      'Auto Repayment': institution.autoRepayment ? 'Yes' : 'No',
      'Created Date': institution.createdAt ? new Date(institution.createdAt).toLocaleDateString() : ''
    }));

    const filename = `institutions_${new Date().toISOString().split('T')[0]}`;

    switch (type) {
      case 'Excel':
        this.exportService.exportToExcel(exportData, filename);
        this._toast.success('Institutions exported to Excel successfully');
        break;
      case 'PDF':
        this.exportService.exportToPDF(exportData, filename, 'All Institutions');
        this._toast.success('Institutions exported to PDF successfully');
        break;
      case 'CSV':
        this.exportService.exportToCSV(exportData, filename);
        this._toast.success('Institutions exported to CSV successfully');
        break;
    }
  }
}
